import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { History, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Loader } from "@/components/shared/loader";
import { ContentPreviewPanel } from "@/features/content/components/ContentPreviewPanel";
import { useContentPermissions } from "@/features/content/hooks/useContentPermissions";
import { useRollbackContentVersion } from "@/features/content/hooks/useContentMutations";
import { contentService } from "@/features/content/services/contentService";
import { formatOptionalArabicDateTime } from "@/lib/formatters";
import { queryKeys } from "@/lib/query-keys";
import type { PageVersionSummary } from "@/types/content";

const TRIGGER_LABELS: Record<string, string> = {
  submit: "إرسال للمراجعة",
  publish: "نشر",
  approve: "موافقة",
  reject: "رفض",
  manual: "يدوي",
  rollback: "استعادة",
};

interface ContentVersionHistoryPanelProps {
  contentType: string;
  pageId: string;
}

export function ContentVersionHistoryPanel({
  contentType,
  pageId,
}: ContentVersionHistoryPanelProps) {
  const { canApprove, canPublish } = useContentPermissions();
  const canRollback = canApprove || canPublish;
  const rollback = useRollbackContentVersion();
  const [previewVersion, setPreviewVersion] = useState<number | null>(null);
  const [rollbackVersion, setRollbackVersion] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.content.versions(contentType, pageId),
    queryFn: () => contentService.listVersions(contentType, pageId),
  });

  const { data: previewData, isLoading: previewLoading } = useQuery({
    queryKey: queryKeys.content.versionDetail(contentType, pageId, String(previewVersion)),
    queryFn: () =>
      contentService.getVersion(contentType, pageId, String(previewVersion)),
    enabled: previewVersion != null,
  });

  const versions = data?.items ?? [];

  const handleRollback = async (version: number) => {
    await rollback.mutateAsync({
      contentType,
      id: pageId,
      versionId: String(version),
    });
    setRollbackVersion(null);
    setPreviewVersion(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader />
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        لا توجد نسخ محفوظة بعد
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <History className="size-4" />
        سجل النسخ
      </div>
      <div className="max-h-48 space-y-2 overflow-y-auto">
        {versions.map((v: PageVersionSummary) => (
          <div
            key={v.version}
            className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-xs"
          >
            <div className="text-right">
              <div className="font-medium">النسخة {v.version}</div>
              <div className="text-muted-foreground">
                {TRIGGER_LABELS[v.trigger] ?? v.trigger} ·{" "}
                {formatOptionalArabicDateTime(v.createdAt)}
              </div>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setPreviewVersion(previewVersion === v.version ? null : v.version)
                }
              >
                معاينة
              </Button>
              {canRollback && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setRollbackVersion(v.version)}
                >
                  <RotateCcw className="size-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {previewVersion != null && (
        <div className="rounded-md border border-border p-3">
          {previewLoading ? (
            <div className="flex justify-center py-4">
              <Loader size="sm" />
            </div>
          ) : (
            <ContentPreviewPanel
              blocks={previewData?.snapshots?.[0]?.snapshot?.blocks}
              layout={previewData?.snapshots?.[0]?.snapshot?.layout}
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={rollbackVersion != null}
        onOpenChange={(o) => !o && setRollbackVersion(null)}
        title="استعادة نسخة"
        description={`سيتم استعادة مسودة الصفحة إلى النسخة ${rollbackVersion}. لن يتم النشر تلقائيًا.`}
        actionLabel="استعادة"
        isLoading={rollback.isPending}
        onConfirm={() =>
          rollbackVersion != null ? handleRollback(rollbackVersion) : Promise.resolve()
        }
        trigger={<span className="hidden" />}
      />
    </div>
  );
}
